import { zodResolver } from '@hookform/resolvers/zod';
import {
    calendarDateSchema,
    childSexSchema,
    ianaTimeZoneSchema,
    personNameSchema,
} from '@baby-tracker/validation';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router';
import { z } from 'zod';

import { routePaths } from '../app/route-paths.js';
import { Button } from '../components/ui/Button.js';
import { errorText } from '../components/ui/error-text.js';
import { FormError } from '../components/ui/FormError.js';
import fieldStyles from '../components/ui/fields.module.css';
import { SelectField } from '../components/ui/SelectField.js';
import { Spinner } from '../components/ui/Spinner.js';
import { TextField } from '../components/ui/TextField.js';
import { useCreateChild } from '../hooks/useChildren.js';
import { useCreateFamily } from '../hooks/useFamily.js';
import { useMe } from '../hooks/useMe.js';

const familySchema = z.object({
    name: personNameSchema,
    timeZone: ianaTimeZoneSchema,
});
const childSchema = z.object({
    name: personNameSchema,
    dateOfBirth: calendarDateSchema,
    sex: childSexSchema,
});

type FamilyValues = z.infer<typeof familySchema>;
type ChildValues = z.infer<typeof childSchema>;

const sexOptions = [
    { value: 'unspecified', label: 'Prefer not to say' },
    { value: 'female', label: 'Female' },
    { value: 'male', label: 'Male' },
] as const;

function browserTimeZone(): string {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone.length > 0 ? zone : 'Australia/Brisbane';
}

export function OnboardingPage(): ReactElement {
    const me = useMe();
    const navigate = useNavigate();
    const createFamily = useCreateFamily();
    const [familyId, setFamilyId] = useState<string | null>(null);
    const [serverError, setServerError] = useState<string | null>(null);
    const familyForm = useForm<FamilyValues>({
        resolver: zodResolver(familySchema),
        defaultValues: { name: '', timeZone: browserTimeZone() },
    });
    const childForm = useForm<ChildValues>({
        resolver: zodResolver(childSchema),
        defaultValues: { name: '', dateOfBirth: '', sex: 'unspecified' },
    });
    const createChild = useCreateChild(familyId ?? '');

    if (me.isPending) {
        return <Spinner label="Loading your account" />;
    }
    if (me.isSuccess && me.data.families.length > 0 && familyId === null) {
        return <Navigate to={routePaths.today} replace />;
    }

    async function onCreateFamily(values: FamilyValues): Promise<void> {
        setServerError(null);
        try {
            const family = await createFamily.mutateAsync(values);
            setFamilyId(family.id);
        } catch (error) {
            setServerError(errorText(error));
        }
    }

    async function onCreateChild(values: ChildValues): Promise<void> {
        setServerError(null);
        try {
            await createChild.mutateAsync(values);
            await navigate(routePaths.today);
        } catch (error) {
            setServerError(errorText(error));
        }
    }

    if (familyId === null) {
        return (
            <>
                <h1>Create your family</h1>
                <p>A family is the private space your caregivers share.</p>
                <form
                    className={fieldStyles.stack}
                    noValidate
                    onSubmit={(event) => {
                        void familyForm.handleSubmit(onCreateFamily)(event);
                    }}
                >
                    <TextField
                        label="Family name"
                        autoComplete="organization"
                        error={familyForm.formState.errors.name?.message}
                        {...familyForm.register('name')}
                    />
                    <TextField
                        label="Time zone"
                        autoComplete="off"
                        error={familyForm.formState.errors.timeZone?.message}
                        {...familyForm.register('timeZone')}
                    />
                    <FormError message={serverError} />
                    <Button type="submit" loading={familyForm.formState.isSubmitting}>
                        Continue
                    </Button>
                </form>
            </>
        );
    }

    return (
        <>
            <h1>Add a child</h1>
            <p>You can add more children later from Settings.</p>
            <form
                className={fieldStyles.stack}
                noValidate
                onSubmit={(event) => {
                    void childForm.handleSubmit(onCreateChild)(event);
                }}
            >
                <TextField
                    label="Name"
                    autoComplete="off"
                    error={childForm.formState.errors.name?.message}
                    {...childForm.register('name')}
                />
                <TextField
                    label="Date of birth"
                    type="date"
                    error={childForm.formState.errors.dateOfBirth?.message}
                    {...childForm.register('dateOfBirth')}
                />
                <SelectField
                    label="Sex"
                    options={sexOptions}
                    error={childForm.formState.errors.sex?.message}
                    {...childForm.register('sex')}
                />
                <FormError message={serverError} />
                <Button type="submit" loading={childForm.formState.isSubmitting}>
                    Finish
                </Button>
            </form>
        </>
    );
}
